import sanitizeHtml, { type IOptions } from 'sanitize-html';

/**
 * Additional media tags beyond sanitize-html defaults
 */
const MEDIA_TAGS = ['img', 'picture', 'source', 'video', 'audio', 'iframe'];

/**
 * Form tags for interactive forms
 */
const FORM_TAGS = ['form', 'input', 'textarea', 'select', 'option', 'button', 'label', 'fieldset', 'legend'];

/**
 * Common attributes allowed on all elements
 */
const COMMON_ATTRIBUTES = [
  'class',
  'id',
  'style',
  'title',
  'aria-*',
  'role',
  'tabindex',
  'data-*',
];

/**
 * Media-specific attributes by tag
 */
const MEDIA_ATTRIBUTES = {
  img: ['src', 'srcset', 'alt', 'title', 'width', 'height', 'loading', 'sizes'],
  source: ['src', 'srcset', 'type', 'media', 'sizes'],
  video: ['src', 'poster', 'controls', 'autoplay', 'loop', 'muted', 'width', 'height'],
  audio: ['src', 'controls', 'autoplay', 'loop', 'muted'],
  iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'referrerpolicy'],
};

const YOUTUBE_IFRAME_SRC =
  /^(?:https?:)?\/\/(?:(?:www|m)\.)?(?:youtube\.com|youtube-nocookie\.com)\//i;

/**
 * Rich-text iframes are stored as www.youtube.com embeds and do not pass
 * through the Video element. Rewrite them to Privacy Enhanced Mode and
 * keep a referring origin so YouTube can accept the embed.
 */
function transformYouTubeIframe(tagName: string, attribs: Record<string, string>) {
  const src = attribs.src || '';
  if (!YOUTUBE_IFRAME_SRC.test(src)) {
    return { tagName, attribs };
  }

  return {
    tagName,
    attribs: {
      ...attribs,
      src: src.replace(
        /^(?:https?:)?\/\/(?:(?:www|m)\.)?youtube\.com(?=\/)/i,
        'https://www.youtube-nocookie.com',
      ),
      referrerpolicy: 'strict-origin-when-cross-origin',
    },
  };
}

/**
 * Form-specific attributes by tag
 */
const FORM_ATTRIBUTES = {
  form: ['action', 'method', 'name', 'target', 'enctype', 'accept-charset', 'novalidate'],
  input: ['type', 'name', 'value', 'placeholder', 'required', 'disabled', 'readonly', 'maxlength', 'minlength', 'pattern', 'min', 'max', 'step', 'checked', 'autocomplete'],
  textarea: ['name', 'rows', 'cols', 'placeholder', 'required', 'disabled', 'readonly', 'maxlength', 'minlength'],
  select: ['name', 'required', 'disabled', 'multiple', 'size'],
  option: ['value', 'selected', 'disabled'],
  button: ['type', 'name', 'value', 'disabled'],
  label: ['for'],
  fieldset: ['disabled'],
  legend: [],
};

/**
 * Script tag attributes (only used when allowScripts is true)
 */
const SCRIPT_ATTRIBUTES = ['type', 'src', 'async', 'defer', 'crossorigin', 'integrity'];

/**
 * Sanitizes HTML content while preserving safe tags and attributes.
 *
 * @param html - The HTML string to sanitize
 * @param allowScripts - Whether to allow <script> tags (default: false)
 * @returns Sanitized HTML string
 */
export function cleanHTML(html: string, allowScripts: boolean = false): string {
  // Get defaults from sanitize-html
  const defaultTags = Array.isArray(sanitizeHtml.defaults.allowedTags)
    ? sanitizeHtml.defaults.allowedTags
    : [];

  const defaultAttributes =
    typeof sanitizeHtml.defaults.allowedAttributes === 'object'
      ? sanitizeHtml.defaults.allowedAttributes
      : {};

  // Build base configuration
  const baseOptions: IOptions = {
    allowedTags: [...defaultTags, ...MEDIA_TAGS, ...FORM_TAGS],
    allowedAttributes: {
      ...defaultAttributes,
      ...MEDIA_ATTRIBUTES,
      ...FORM_ATTRIBUTES,
      '*': COMMON_ATTRIBUTES,
    },
    transformTags: {
      iframe: transformYouTubeIframe,
    },
  };

  // If scripts are allowed, extend configuration
  if (allowScripts) {
    const allowedTags = Array.isArray(baseOptions.allowedTags) 
      ? [...baseOptions.allowedTags, 'script']
      : ['script'];
      
    return sanitizeHtml(html, {
      ...baseOptions,
      allowedTags,
      allowedAttributes: {
        ...baseOptions.allowedAttributes,
        script: SCRIPT_ATTRIBUTES,
      },
      allowVulnerableTags: true,
      allowedSchemes: ['http', 'https', 'data', 'blob'],
      allowedSchemesByTag: {
        script: ['http', 'https', 'data', 'blob'],
      },
      allowProtocolRelative: true,
    });
  }

  // Default sanitization (scripts not allowed)
  return sanitizeHtml(html, baseOptions);
}
